import {
	canStartRecording,
	closeSourcePicker,
	openSourcePicker,
	pauseRecording,
	resetRecording,
	resumeRecording,
	retryUpload,
	startRecording,
	stopRecording,
	useRecorderStore,
} from "./recorder-session";

export type VideoRecorderController = ReturnType<typeof useVideoRecorder>;

/**
 * Read the one recording the app has, and the controls for it.
 *
 * The hook owns no state. Everything lives in `recorder-session`, in module scope, so the
 * recording survives the component that started it — which is what lets the author navigate
 * away from the wiki page and keep recording.
 *
 * @returns The recording's state and the functions that drive it.
 */
export function useVideoRecorder() {
	/** Store */
	const status = useRecorderStore((state) => state.status);
	const video = useRecorderStore((state) => state.video);
	const videoUrl = useRecorderStore((state) => state.videoUrl);
	const isSourcePickerOpen = useRecorderStore((state) => state.isSourcePickerOpen);
	const target = useRecorderStore((state) => state.target);
	const progress = useRecorderStore((state) => state.progress);
	const didReachLimit = useRecorderStore((state) => state.didReachLimit);

	/** Computed */
	const isRecording = status === "recording" || status === "paused";

	/** Return */
	return {
		status,
		didReachLimit,
		video,
		videoUrl,
		target,
		progress,
		hasVideo: !!video,
		isRecording,
		isAssembling: status === "assembling",
		isPaused: status === "paused",
		isUploading: status === "uploading",
		isUploaded: status === "uploaded",
		hasFailed: status === "failed",
		/** False while a recording, an upload or an unshipped take still holds the recorder. */
		canStart: canStartRecording(),
		onRetryUpload: retryUpload,
		onStartRecording: startRecording,
		onStopRecording: stopRecording,
		onPauseRecording: pauseRecording,
		onResumeRecording: resumeRecording,
		onReset: resetRecording,
		onToggleRecording: () => (isRecording ? stopRecording() : openSourcePicker()),
		disclosure: {
			isOpen: isSourcePickerOpen,
			onOpen: openSourcePicker,
			onClose: closeSourcePicker,
		},
	};
}
